import JsonModels from '@/ts/models.json';
import type { JsonModel } from '@/types/columnTypes';
import type { TableColumnStaticProps } from '@/types/tableTypes';
import { filterControlMappings } from '@/components/FilterBuilder/filterControlRegistry';
import { columnHeaderLabel } from '@/utils/columnLabels';

/**
 * Pure models.json → column derivation, shared by {@link usePersistentTable}
 * (the per-entity table/cards state) and one-off consumers that need an
 * arbitrary entity's columns outside the selected-entity context (e.g. the
 * id-column "open in side peek" record inspector).
 */
export function entityModel(entityId: string): JsonModel | undefined {
    return JsonModels[entityId.replace(/\s+/g, '') as keyof typeof JsonModels] as
        | JsonModel
        | undefined;
}

/**
 * Whether models.json marks the entity as searchable (generated `_slug` is only
 * emitted for SearchableContract models, i.e. entities with a search endpoint).
 */
export function isSearchableEntity(entityId: string): boolean {
    const slug = entityModel(entityId)?._slug;
    return typeof slug === 'string' && slug.length > 2;
}

/** The slugs of the searchable entities, built on first use. */
let searchableSlugs: Set<string> | null = null;

/** Whether a slug, such as `location_zone_custom_playlist`, names a searchable entity. */
export function isSearchableSlug(slug: string): boolean {
    searchableSlugs ??= new Set(
        Object.keys(JsonModels)
            .filter(isSearchableEntity)
            .map((entityId) => entitySlugFor(entityId) as string),
    );
    return searchableSlugs.has(slug);
}

/**
 * Models whose name in the interface is not their model name. The hardware
 * `Player` is called a "Unit", so that "Unit 31" is never mistaken for a
 * Location Zone Player, which is the record people call "Player 2007" (SAU-437).
 */
const ENTITY_DISPLAY_NAME_OVERRIDES: Record<string, string> = {
    Player: 'Unit',
};

/**
 * Human-friendly model name from models.json `_displayName` (e.g.
 * LocationZoneCustomPlaylist → "Custom Playlist"); falls back to the raw
 * entity id for models without one.
 */
export function entityDisplayNameFor(entityId: string): string {
    const renamed = ENTITY_DISPLAY_NAME_OVERRIDES[entityId];
    if (renamed !== undefined) {
        return renamed;
    }
    const displayName = entityModel(entityId)?._displayName;
    if (typeof displayName === 'string' && displayName.length > 2) {
        // Generated as a quoted literal, e.g. "'Custom Playlist'".
        return displayName.slice(1, -1);
    }
    return entityId;
}

/** The API slug for an entity, when models.json knows it (searchable entities only). */
export function entitySlugFor(entityId: string): string | null {
    const model = entityModel(entityId);
    const slug = model?._slug;
    if (typeof slug === 'string' && slug.length > 2) {
        // Generated as a quoted literal, e.g. "'location_zone_player'".
        return slug.slice(1, -1);
    }
    // Non-searchable models (no _slug) follow the same snake_case convention.
    return entityId ? entityId.replace(/(?<!^)[A-Z]/g, '_$&').toLowerCase() : null;
}

/**
 * Columns whose models.json type is wrong for filtering, keyed by entity and column id.
 * A schedule's dates are plain calendar dates that the backend casts as strings.
 * Only a Date column gets the date picker and the is-before and is-after operators.
 * The schedule's time column stays a string on purpose.
 */
const DATA_TYPE_OVERRIDES: Record<string, Record<string, string>> = {
    LocationZoneCustomPlaylistSchedule: {
        date: 'Date',
        end_date: 'Date',
        recurring_start_date: 'Date',
        recurring_end_date: 'Date',
    },
};

/**
 * Fields that hold a credential. They are never shown or offered, on any record
 * type: not in Details, Customize, a table, Columns, Export or the Filter picker.
 * models.json still lists them, because it is generated from each model's casts.
 * The backend hides them from its responses too, but the interface does not
 * rely on that.
 *
 * - `auth_tok` is a hardware Player's credential for the player server.
 * - `password` and `crypted_password` on User, and `laravel_password` on
 *   ContactLogin, are password hashes. Showing a hash helps no one.
 * - `remember_token` is the "remember me" login token, on User and ContactLogin.
 *
 * A contact login's `password` is the one exception. It is listed in
 * {@link READABLE_PASSWORD_FIELDS} and is shown as a password field.
 *
 * Every surface builds its columns through {@link buildEntityColumns}, so
 * leaving a field out there leaves it out everywhere.
 */
export const SECRET_FIELDS: readonly string[] = ['auth_tok', 'password', 'crypted_password', 'laravel_password', 'remember_token'];

/**
 * Login passwords that support reads out to a client, keyed by entity. They
 * are not hidden. Every surface draws them as a password field: eight dots,
 * with an eye button that shows the password and hides it again. They cannot
 * be sorted or filtered, and Export offers them unticked.
 *
 * - `ContactLogin.password` is the password the client types into the player
 *   app. A User's `password` is a hash, so it stays in {@link SECRET_FIELDS}.
 */
export const READABLE_PASSWORD_FIELDS: Readonly<Record<string, readonly string[]>> = {
    ContactLogin: ['password'],
};

/** The data type a readable password's column carries, so every surface knows to mask it. */
export const PASSWORD_DATA_TYPE = 'password';

/** Whether a field is a login password drawn as a password field ({@link READABLE_PASSWORD_FIELDS}). */
export function isReadablePasswordField(entityId: string, field: string): boolean {
    return READABLE_PASSWORD_FIELDS[entityId]?.includes(field) ?? false;
}

/** Whether a field holds a credential that is never shown ({@link SECRET_FIELDS}). */
export function isSecretField(entityId: string, field: string): boolean {
    return SECRET_FIELDS.includes(field) && !isReadablePasswordField(entityId, field);
}

/**
 * Fields the interface never shows, keyed by entity. Like {@link SECRET_FIELDS}, they are left
 * out of Details, Customize, a table, cards, Columns, Export and the Filter picker.
 *
 * - A schedule's insert type. Every schedule is written as Replace, and no one picks it.
 */
export const RETIRED_FIELDS: Readonly<Record<string, readonly string[]>> = {
    LocationZoneCustomPlaylistSchedule: ['custom_playlist_insert_type', 'custom_playlist_insert_type_id'],
};

/** Whether the interface never shows a field: a credential or a retired field. */
export function isNeverShownField(entityId: string, field: string): boolean {
    return isSecretField(entityId, field) || (RETIRED_FIELDS[entityId]?.includes(field) ?? false);
}

export function buildEntityColumns(entityId: string): TableColumnStaticProps[] {
    const currentModel = entityModel(entityId);
    if (!currentModel) {
        return [];
    }

    const prohibitedFields = currentModel._prohibited || [];
    const filledFields = currentModel._filled || [];
    const uniqueFields = currentModel._unique || [];
    const nullableFields = (currentModel as { _nullable?: string[] })._nullable || [];
    const columnMappings = currentModel._columnNameMappings || {};
    const filterFieldMappings = currentModel._filterFieldMappings || {};
    const modelFilterControls = filterControlMappings[entityId] || {};
    const dataTypeOverrides = DATA_TYPE_OVERRIDES[entityId] || {};

    return Object.entries(currentModel)
        .filter(([id]) => !id.startsWith('_') && !isNeverShownField(entityId, id))
        .map(([id, modelType]): TableColumnStaticProps => {
            const key = filterFieldMappings[id] || id;
            const header = columnMappings[id] || id;
            const isPassword = isReadablePasswordField(entityId, id);
            return {
                id,
                key,
                header: columnHeaderLabel(header),
                dataType: isPassword ? PASSWORD_DATA_TYPE : (dataTypeOverrides[id] ?? (modelType as string)),
                field: filterFieldMappings[id] || id,
                isFilled: filledFields.includes(id),
                isProhibited: prohibitedFields.includes(id),
                isUnique: uniqueFields.includes(id),
                isNullable: nullableFields.includes(id),
                // Sorting or filtering by a password would give it away a letter at a time.
                isSortable: !isPassword,
                isFilterable: !isPassword,
                ...(modelFilterControls[id] && { filterControl: modelFilterControls[id] }),
            };
        });
}
